/**
 * AssessmentDesignSchemes.jsx — Phase 2A-4
 * State machine: DRAFT → PUBLISHED → LOCKED
 * Designer roles for create/publish/lock.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Plus, RefreshCw, Lock, X } from "lucide-react";
import { toast } from "sonner";
import { AssessmentDesignShell, useIsDesigner } from "../components/assessment-design/AssessmentDesignShell";
import { LifecycleBadge } from "../components/assessment-design/LifecycleBadge";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
function authH() { return { "Authorization": `Bearer ${localStorage.getItem("academiaos_token")}`, "Content-Type": "application/json" }; }
function apiError(e, fb = "Error") {
  const d = e?.detail || e?.message || fb;
  if (Array.isArray(d)) return d.map(x => x.msg || JSON.stringify(x)).join("; ");
  return String(d);
}

const EMPTY_FORM = { code: "", title: "", pass_mark_pct: "" };

export default function AssessmentDesignSchemes() {
  const isDesigner = useIsDesigner();
  const [schemes, setSchemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [actioning, setActioning] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/assessment/schemes`, { headers: authH() });
      if (!r.ok) throw await r.json().catch(() => ({}));
      setSchemes(await r.json());
    } catch (e) {
      toast.error("Failed to load schemes: " + apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { code: form.code, title: form.title };
      if (form.pass_mark_pct) body.pass_mark_pct = parseFloat(form.pass_mark_pct);
      const r = await fetch(`${API}/assessment/schemes`, {
        method: "POST", headers: authH(), body: JSON.stringify(body),
      });
      const data = await r.json();
      if (!r.ok) throw data;
      toast.success(`Scheme "${data.title}" created`);
      setShowCreate(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e) {
      toast.error("Create failed: " + apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleAction = async (scheme, action) => {
    setActioning(`${scheme.id}-${action}`);
    try {
      const r = await fetch(`${API}/assessment/schemes/${scheme.id}/${action}`, {
        method: "POST", headers: authH(),
      });
      const data = await r.json();
      if (!r.ok) throw data;
      toast.success(`Scheme "${data.title}" ${action}ed`);
      load();
    } catch (e) {
      const d = e?.detail;
      if (d?.error === "stale_version") toast.error("Stale version — refresh and retry.");
      else if (d?.error === "permission_denied") toast.error(`Permission denied: ${d.required_roles?.join(", ")}`);
      else toast.error(`${action} failed: ` + apiError(e));
    } finally {
      setActioning(null);
    }
  };

  return (
    <AssessmentDesignShell title="Assessment Schemes" breadcrumb="Schemes">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-slate-400">
          Grading schemes set pass marks and scoring rules.{" "}
          <span className="font-mono text-slate-300">DRAFT → PUBLISHED → LOCKED</span>
        </p>
        <div className="flex items-center gap-2">
          <button onClick={load} disabled={loading} data-testid="schemes-refresh-btn"
            className="p-2 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition-colors">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
          {isDesigner && (
            <button onClick={() => setShowCreate(true)} data-testid="scheme-create-button"
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-sky-600 hover:bg-sky-500 text-white transition-colors">
              <Plus className="w-4 h-4" /> New Scheme
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1,2,3].map(i => <div key={i} className="h-16 rounded-lg bg-slate-800/40 animate-pulse" />)}
        </div>
      ) : schemes.length === 0 ? (
        <div data-testid="schemes-empty" className="text-center py-16 text-slate-500 text-sm">
          No grading schemes yet.
          {isDesigner && <button onClick={() => setShowCreate(true)} className="block mt-2 text-sky-400 hover:text-sky-300 mx-auto">Create first scheme →</button>}
        </div>
      ) : (
        <div data-testid="schemes-list" className="space-y-2">
          {schemes.map(sc => (
            <div key={sc.id} data-testid={`scheme-row-${sc.id}`}
              className="flex items-center gap-4 p-4 rounded-xl border border-slate-800 bg-slate-900/50 hover:border-slate-700 transition-colors">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-sm font-semibold text-slate-100 truncate">{sc.title}</span>
                  <LifecycleBadge status={sc.status} />
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-500">
                  <span className="font-mono">{sc.code}</span>
                  {sc.pass_mark_pct != null && <span>Pass: {sc.pass_mark_pct}%</span>}
                </div>
              </div>
              {isDesigner && (
                <div className="flex items-center gap-2 flex-shrink-0">
                  {sc.status === "DRAFT" && (
                    <button
                      onClick={() => handleAction(sc, "publish")}
                      disabled={actioning === `${sc.id}-publish`}
                      data-testid={`scheme-publish-btn-${sc.id}`}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-900/40 hover:bg-emerald-800/60 text-emerald-300 border border-emerald-700/50 disabled:opacity-50 transition-colors">
                      {actioning === `${sc.id}-publish` ? "Publishing…" : "Publish"}
                    </button>
                  )}
                  {sc.status === "PUBLISHED" && (
                    <button
                      onClick={() => handleAction(sc, "lock")}
                      disabled={actioning === `${sc.id}-lock`}
                      data-testid={`scheme-lock-btn-${sc.id}`}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium bg-violet-900/40 hover:bg-violet-800/60 text-violet-300 border border-violet-700/50 disabled:opacity-50 transition-colors flex items-center gap-1">
                      <Lock className="w-3 h-3" />
                      {actioning === `${sc.id}-lock` ? "Locking…" : "Lock"}
                    </button>
                  )}
                  {sc.status === "LOCKED" && (
                    <span className="text-xs text-slate-500 flex items-center gap-1">
                      <Lock className="w-3 h-3 text-violet-500" /> Locked
                    </span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div data-testid="scheme-create-modal" className="w-full max-w-md rounded-2xl border border-slate-700 bg-slate-900 shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
              <h2 className="text-base font-semibold text-slate-100">Create Grading Scheme</h2>
              <button onClick={() => { setShowCreate(false); setForm(EMPTY_FORM); }}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800">
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Code *</label>
                <input data-testid="scheme-code-input" value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value }))}
                  required placeholder="e.g. GS-2026-UG"
                  className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 placeholder:text-slate-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Title *</label>
                <input data-testid="scheme-title-input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  required placeholder="Grading Scheme Title"
                  className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 placeholder:text-slate-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Pass Mark %</label>
                <input data-testid="scheme-pass-mark-input" type="number" min="0" max="100" step="0.1"
                  value={form.pass_mark_pct} onChange={e => setForm(f => ({ ...f, pass_mark_pct: e.target.value }))}
                  placeholder="e.g. 50"
                  className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 placeholder:text-slate-500" />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => { setShowCreate(false); setForm(EMPTY_FORM); }}
                  className="px-4 py-2 rounded-lg text-sm bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 transition-colors">Cancel</button>
                <button type="submit" disabled={saving} data-testid="scheme-create-submit-btn"
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-sky-600 hover:bg-sky-500 text-white disabled:opacity-50 transition-colors">
                  {saving ? "Creating…" : "Create Scheme"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AssessmentDesignShell>
  );
}
